'use client'
/**
 * @credit https://github.com/Effect-TS/effect-days-2025-workshop/blob/main/src/demos/section-2/react-solution/Runtime.tsx
 */

import { Effect, Layer, ManagedRuntime } from 'effect'
import * as React from 'react'

const defaultMemoMap = Effect.runSync(Layer.makeMemoMap)

type RuntimeReference<R, E> = {
  timeout: ReturnType<typeof setTimeout> | undefined
  readonly runtime: ManagedRuntime.ManagedRuntime<R, E>
}

type MakeReactRuntimeOptions = {
  readonly disposeTimeout?: number
  readonly memoMap?: Layer.MemoMap
}

export function makeReactRuntime<R, E>(
  createLayer: () => Layer.Layer<R, E>,
  {
    disposeTimeout = 500,
    memoMap = defaultMemoMap,
  }: MakeReactRuntimeOptions = {},
) {
  const Context = React.createContext<ManagedRuntime.ManagedRuntime<R, E>>(
    ManagedRuntime.make(createLayer(), { memoMap }),
  )

  const Provider = ({ children }: React.PropsWithChildren) => {
    const runtimeReference = React.useRef<RuntimeReference<R, E>>(
      undefined as any,
    )
    if (!runtimeReference.current) {
      runtimeReference.current = {
        timeout: undefined,
        runtime: ManagedRuntime.make(createLayer(), { memoMap }),
      }
    }

    React.useEffect(() => {
      const reference = runtimeReference.current
      if (reference.timeout) {
        clearTimeout(reference.timeout)
        reference.timeout = undefined
      }
      return () => {
        reference.timeout = setTimeout(
          () => reference.runtime.dispose(),
          disposeTimeout,
        )
      }
    }, []) // oxlint-disable-line react-hooks/exhaustive-deps -- runtimeReference is a stable ref

    return (
      <Context.Provider value={runtimeReference.current.runtime}>
        {children}
      </Context.Provider>
    )
  }

  const use = () => React.useContext(Context)

  return { Context, Provider, use } as const
}

export { defaultMemoMap }
